import { FC, useEffect } from "react";
import {
  AdminElectronicGeneralMeetingController,
  ElectronicGeneralMeeting,
  LiveVotingResults,
  ShareholderVoteRow,
} from "./electronicGeneralMeetingDomain";
import { User } from "@/types/UserInfo";
import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Box, VBox } from "@/components-v2/Box";
import { Icon } from "@/components-v2/Icon";
import { IconButton, LinkButton, ToggleButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { GeneralMeetingStatusTag } from "./AdminElectronicGeneralMeetingPage";
import {
  DbotEvent,
  DbotEventDispatcher,
  GeneralMeetingIndividualVote,
  voteLabel,
} from "@/dbot/common/commonDomain";
import { decisionLabel, GeneralMeetingVoteType } from "./generalMeetingDomain";
import { MTextInput } from "@/components-v2/TextInput";
import { FilterToggleButton } from "./AdminElectronicGeneralMeetingAttendanceDialog";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";

function votingStatus(
  voteType?: GeneralMeetingVoteType,
  decisionId?: string,
  meeting?: ElectronicGeneralMeeting,
) {
  switch (voteType) {
    case "meetChair":
      return meeting?.meetingChairStatus;
    case "approveNoticeAndAgenda":
      return meeting?.approveAgendaStatus;
    default:
      return meeting?.decisionStatuses?.find((s) => s.decisionId === decisionId)?.status;
  }
}

export const VotingDialog = observer(
  ({
    title,
    controller,
    projectId,
    meeting,
    user,
    data,
    dispatch,
    onUpdateVotingStatus,
    next,
    prev,
  }: {
    title: TMessage;
    controller: AdminElectronicGeneralMeetingController;
    projectId: string;
    meeting: ElectronicGeneralMeeting;
    user: User;
    data: LiveVotingResults;
    dispatch: DbotEventDispatcher;
    onUpdateVotingStatus: () => void;
    next?: DbotEvent[];
    prev?: DbotEvent[];
  }) => {
    const { t } = useTranslation();
    useEffect(() => {
      controller.loadPowerOfAttorneyData(projectId);
    }, [controller, projectId]);
    const status = votingStatus(controller.currentVoteType, controller.decisionId, meeting);
    return (
      <Dialog
        title={t(title)}
        fullscreen
        onCancel={() => controller.hideGeneralMeetingDialogs()}
        actions={<div />}
        headerElement={
          <Box alignItems={"center"} justifyContent={"space-between"} grow>
            <Box gap={"md"}>
              <GeneralMeetingStatusTag status={status} />
              <LinkButton label={t(decisionLabel(status))} onClick={onUpdateVotingStatus} />
            </Box>
            <Box alignItems={"center"}>
              <IconButton
                icon={"caret-down"}
                color={"inherit"}
                tooltip={t("next")}
                disabled={!next}
                onClick={() => {
                  if (next !== undefined) {
                    dispatch(next);
                  }
                }}
              />

              <IconButton
                icon={"caret-up"}
                color={"inherit"}
                tooltip={t("previous")}
                disabled={!prev}
                onClick={() => {
                  if (prev !== undefined) {
                    dispatch(prev);
                  }
                }}
              />
            </Box>
          </Box>
        }
      >
        <VBox>
          <ShareholderVotingFilterBar controller={controller} data={data} />
          <ShareholderVotingTable
            controller={controller}
            projectId={projectId}
            meeting={meeting}
            user={user}
            data={data}
          />
        </VBox>
      </Dialog>
    );
  },
);

const ShareholderVotingFilterBar: FC<{
  controller: AdminElectronicGeneralMeetingController;
  data: LiveVotingResults;
}> = observer(({ controller, data }) => {
  const { t } = useTranslation();
  return (
    <Box gap={"xl"} alignItems={"center"}>
      <MTextInput
        model={controller.shareholderNameFilter}
        placeholder={t("general-meeting.attendance.shareholderNameFilter")}
        autofocus
        inline
        type={"search"}
      />
      <Box>
        <FilterToggleButton
          icon={"calendar-check"}
          shareholders={data.shareholders}
          shareTypes={controller.shareTypes}
          nominalValue={controller.shareNominalValue}
          sharesForShareholders={controller.sharesForShareholders}
          totalShareCapital={data.totalShareCapital}
          headingLabel={"general-meeting.voting.participating"}
          selected={controller.shareholderVotingFilter === "participating"}
          onClick={() => controller.setSelectedVotingFilter("participating")}
        />
        <FilterToggleButton
          icon={"question"}
          shareholders={data.shareholdersNotVoted}
          shareTypes={controller.shareTypes}
          nominalValue={controller.shareNominalValue}
          sharesForShareholders={controller.sharesForShareholders}
          headingLabel={"general-meeting.voting.shareholdersNotVoted"}
          totalShareCapital={controller.totalShareCapital}
          selected={controller.shareholderVotingFilter === "shareholdersNotVoted"}
          onClick={() => controller.setSelectedVotingFilter("shareholdersNotVoted")}
        />
        <FilterToggleButton
          icon={"thumbs-up"}
          shareholders={data.shareholdersFor}
          shareTypes={controller.shareTypes}
          nominalValue={controller.shareNominalValue}
          sharesForShareholders={controller.sharesForShareholders}
          headingLabel={"general-meeting.voting.shareholdersFor"}
          totalShareCapital={controller.totalShareCapital}
          selected={controller.shareholderVotingFilter === "shareholdersFor"}
          onClick={() => controller.setSelectedVotingFilter("shareholdersFor")}
        />
        <FilterToggleButton
          icon={"thumbs-down"}
          shareholders={data.shareholdersAgainst}
          shareTypes={controller.shareTypes}
          nominalValue={controller.shareNominalValue}
          sharesForShareholders={controller.sharesForShareholders}
          headingLabel={"general-meeting.voting.shareholdersAgainst"}
          totalShareCapital={controller.totalShareCapital}
          selected={controller.shareholderVotingFilter === "shareholdersAgainst"}
          onClick={() => controller.setSelectedVotingFilter("shareholdersAgainst")}
        />
      </Box>
    </Box>
  );
});

const ShareholderVotingTable: FC<{
  controller: AdminElectronicGeneralMeetingController;
  projectId: string;
  meeting: ElectronicGeneralMeeting;
  user: User;
  data: LiveVotingResults;
}> = observer(({ projectId, controller, meeting, data }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();
  const tableColumns: TableColumn<ShareholderVoteRow>[] = [
    {
      name: "name",
      label: t(controller.shareholderVotingColumnLabel),
      align: "left",
      getValue: (s) => (
        <ShareholderNameVotingDetail
          name={s.name}
          gmVote={controller.getVoteForShareholder(s.shareholderId, meeting)}
        />
      ),
    },
    {
      name: "representative",
      label: t("general-meeting.voting.representative"),
      align: "left",
      getValue: (s) =>
        controller.hasPoa(s.shareholderId) ? (
          <VBox gap={0}>
            <div>{s.representative}</div>
            <small className="subtle">{t("general-meeting.attendance.powerOfAttorney")}</small>
          </VBox>
        ) : (
          s.representative
        ),
    },
    {
      name: "vote",
      label: t("general-meeting.voting.vote"),
      align: "left",
      getValue: (s) => (
        <RegisterVoteButtons
          shareholderId={s.shareholderId}
          controller={controller}
          projectId={projectId}
          meeting={meeting}
        />
      ),
    },
    {
      name: "birthDateOrOrganisationNumber",
      label: t("general-meeting.birthDateOrOrganisationNumber"),
      align: "left",
    },
    {
      name: "numberOfShares",
      label: t("general-meeting.numberOfShares"),
      getValue: (s) => formatNumber(s.numberOfShares),
    },
    {
      name: "percentageOfShares",
      label: t("general-meeting.percentageOfShares"),
      getValue: (s) => formatPercent(s.percentageOfShares),
    },
    {
      name: "shareCapital",
      label: t("general-meeting.shareCapital"),
      getValue: (s) => formatNumber(s.shareCapital, { maximumFractionDigits: 2 }),
    },
    {
      name: "email",
      label: t("general-meeting.email"),
      align: "left",
    },
  ];

  return (
    <DataTable
      variant={"COMPACT"}
      tableColumns={tableColumns}
      tableData={controller.createVotingTableData(data)}
      getId={(row) => row.id}
    />
  );
});

const RegisterVoteButtons: FC<{
  controller: AdminElectronicGeneralMeetingController;
  shareholderId: number;
  projectId: string;
  meeting: ElectronicGeneralMeeting;
}> = observer(({ shareholderId, controller, projectId, meeting }) => {
  const { t } = useTranslation();
  const gmVote = controller.getVoteForShareholder(shareholderId, meeting);
  const vote = gmVote?.vote;
  return vote ? (
    <Box alignItems={"center"}>
      {t(voteLabel(vote))}
      <IconButton
        icon={"arrow-counter-clockwise"}
        tooltip={t("reset")}
        onClick={() => controller.deleteVoteAsAdmin(shareholderId, projectId, meeting)}
        loading={controller.isDeletingVotesForShareholder(shareholderId)}
        disabled={controller.isDeletingVotesForShareholder(shareholderId)}
      />
    </Box>
  ) : (
    <Box>
      <ToggleButton
        icon={"thumbs-up"}
        label={t("documents.generalMeetingVoting.voteFor")}
        variant={"small"}
        onClick={() => controller.voteAsAdmin(shareholderId, "for", projectId, meeting)}
        loading={controller.isRegisteringVotesForShareholder(shareholderId)}
        selected={vote === "for"}
      />
      <ToggleButton
        icon={"thumbs-down"}
        label={t("documents.generalMeetingVoting.voteAgainst")}
        variant={"small"}
        onClick={() => controller.voteAsAdmin(shareholderId, "against", projectId, meeting)}
        loading={controller.isRegisteringVotesForShareholder(shareholderId)}
        selected={vote === "against"}
      />
    </Box>
  );
});

function ShareholderNameVotingDetail({
  name,
  gmVote,
}: {
  name: string;
  gmVote?: GeneralMeetingIndividualVote;
}) {
  const { t } = useTranslation();
  const vote = gmVote?.vote;
  return (
    <Box alignItems={"center"} gap={"sm"}>
      {vote === "for" && <Icon name={"thumbs-up"} weight={"bold"} color={"success"} />}
      {vote === "against" && <Icon name={"thumbs-down"} weight={"bold"} color={"error"} />}
      {vote === undefined && <Icon name={"question"} color={"subtle"} />}
      <VBox gap={0}>
        <div>{name}</div>
        <small className="subtle">{vote ? t(voteLabel(vote)) : t("noVote")}</small>
      </VBox>
    </Box>
  );
}
